import React, { useEffect, useRef } from "react";
import { Animated, LayoutChangeEvent, Platform, ViewStyle } from "react-native";

type StickyPriceWrapperProps = {
  show: boolean;
  children: React.ReactNode;
  topMargin: number;
  onLayout?: (event: LayoutChangeEvent) => void;
};

const StickyPriceWrapper = React.forwardRef<any, StickyPriceWrapperProps>(
  ({ show, children, topMargin, onLayout }, ref) => {
    const opacity = useRef(new Animated.Value(0)).current;
    const translateY = useRef(new Animated.Value(-20)).current;

    useEffect(() => {
      Animated.parallel([
        Animated.timing(opacity, {
          toValue: show ? 1 : 0,
          duration: 200,
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(translateY, {
          toValue: show ? 0 : -20,
          duration: 200,
          useNativeDriver: Platform.OS !== "web",
        }),
      ]).start();
    }, [show]);

    // NOTE: width and zIndex are kept constant to avoid a JS-thread layout
    // pass on the absolutely-positioned sibling of the PDP ScrollView. On
    // older Android (e.g. Android 8.1.0 / Oppo A3s) toggling width 0 -> 100%
    // and zIndex -20 -> 100 in the same render as a sibling layout caused
    // the KeyboardAwareScrollView to reset its contentOffset to 0 (page
    // jumping to top the moment the sticky header appeared while scrolling).
    // Visibility is now driven purely by native-driven opacity / translateY,
    // with `pointerEvents` ensuring the invisible wrapper does not capture
    // touches meant for the content underneath.
    const animatedStyle: Animated.WithAnimatedObject<ViewStyle> = {
      position: "absolute",
      top: topMargin,
      left: 0,
      right: 0,
      zIndex: show ? 100 : -20,
      opacity: show ? opacity : 0,
      width: show ? "100%" : 0,
      backgroundColor: "white",
      transform: [{ translateY }],
      justifyContent: "center",
      alignItems: "center",
      display: "flex",
    };

    return (
      <Animated.View ref={ref} style={animatedStyle} onLayout={onLayout}>
        {children}
      </Animated.View>
    );
  }
);

StickyPriceWrapper.displayName = "StickyPriceWrapper";

export default StickyPriceWrapper;
